/**
 * src/stores/syncQueueBridge.ts
 *
 * Cycle-free bridge between the producer (useProjectStore action bodies) and
 * the IDB-backed change queue + useSyncStore.pendingCount.
 *
 * WHY the dynamic import: useSyncStore.ts already carries a top-level static
 * import of useProjectStore (to apply flush()'s server merge via setRooms).
 * If this bridge — called FROM useProjectStore's action bodies — took a
 * static import of useSyncStore, the module graph would close the exact
 * `useProjectStore → syncQueueBridge → useSyncStore → useProjectStore` cycle
 * that Phase 34-01 restructured `migrateProjectStore` into its own leaf
 * module (src/stores/projectMigrations.ts) specifically to avoid (recorded
 * in STATE.md's Phase 34-01 decision). The dynamic `await import(...)`
 * inside the function body keeps the STATIC module graph acyclic while
 * still bumping `pendingCount` on every enqueue, as 36-CONTEXT.md Area 4
 * requires.
 */
import { enqueueChange, peekQueue } from '@/shared/lib/changeQueue';
import type { ChangeRecord } from '@/entities/sync/model/types';

/**
 * Enqueue a change record and publish the resulting queue length through
 * useSyncStore.pendingCount.
 *
 * Never throws: the count publication is wrapped so a failure there cannot
 * turn a successful (or gracefully-degraded, per changeQueue's own T-04-10
 * contract) enqueue into a rejection for the caller.
 */
export async function enqueueProjectChange(record: ChangeRecord): Promise<void> {
  await enqueueChange(record);
  try {
    const { useSyncStore } = await import('@/stores/useSyncStore');
    const count = (await peekQueue()).length;
    useSyncStore.getState().setPendingCount(count);
  } catch (e) {
    console.warn('[syncQueueBridge] pendingCount publication failed', e);
  }
}
